import React, { useEffect, memo } from 'react';
import { Text, View } from 'react-native';
import Input from '@/components/General/inputs/Input';
import InputSection from '@/components/General/inputs/InputSection';
import { saveSong } from '@/data/songs';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Validator from "@/utils/InputValidator";
import Toast from 'react-native-toast-message'
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import SectionTitle from '@/components/General/SectionTitle';
import FileInput from '@/components/General/inputs/FileInput';
import { useLocalSearchParams } from 'expo-router';
import { useRouter } from 'expo-router';
import { redirectToDefault } from '@/utils/constants/linking';
import { useEditScreenStore } from '@/data/store/useEditScreenStore';
import ModalWrapper from '@/components/General/ModalWrapper';
import { StyleSheet } from 'react-native-unistyles';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';

const EMPTY_DRAFT = {};

export default function AddSongModal() {
    const { id, fromScreen, fromStoreKey } = useLocalSearchParams();
    const storeKey = id || "new";

    /*** Theme & Dimensions ***/
    const router = useRouter()


    /*** Queries ***/
    const queryClient = useQueryClient()
    const { data: loggedInUser } = Query.people.loggedInUser()
    const { data: songToEdit } = Query.songs.byId({id, options: {enabled: !!id, placeholderData: {}}})

    const setEditScreenData = useEditScreenStore(s => s.setEditScreenData)
    const removeEditScreenData = useEditScreenStore(s => s.removeEditScreenData)
    const songDetails = useEditScreenStore(s => s.AddSongModal?.[storeKey]) || EMPTY_DRAFT

    const saveSongMutation = useMutation({
        mutationFn: saveSong,
        onSuccess: (data) => {
            queryClient.invalidateQueries({queryKey: QueryKeys.songs.all()});
            removeEditScreenData("AddSongModal", storeKey)

            if(fromScreen){
                useEditScreenStore.getState().dataChangeFunction?.(data)
                router.dismissTo({pathname:fromScreen, params: fromStoreKey && fromStoreKey != "new" ? {id: fromStoreKey} : undefined});
            }
            else{
                redirectToDefault(router, "AddSongModal")
            }
        },
    })


    useEffect(() => {
        if(songToEdit){
            setSongDetails({
                ...songToEdit,
            });
        }
    }, [songToEdit]);

    const setSongDetails = (new_details) => {
        setEditScreenData("AddSongModal", storeKey, new_details)
    }

    const UploaderDropZone = memo(({assets, name, multiple = false}) => {
        return <FileInput
            multiple={multiple}
            showPhotoLibrary
            assets={assets}
            onSelect={assets => {
                setSongDetails({
                    [name]: !multiple ? assets[0] : [
                        ...(songDetails.files || []),
                        ...assets
                    ]
                })
            }}
            onRemoveAsset={(uri, index) => {
                setSongDetails(prev => {
                    if(name != "files"){
                        delete prev[name];
                    }
                    else{
                        delete prev[name][index];
                    }

                    return {...prev}
                })
            }}
        />
    }, [songDetails])

    return (
        <ModalWrapper 
            loading={saveSongMutation.isPending} 
            title="New Song"
            onConfirm={() => {
                if(!Validator.areStringsPopulated([songDetails.name, songDetails.artist])){
                    Toast.show({
                        type: 'error',
                        text2: `Make sure your song has a Title and an Artist!`
                    });

                    return true;
                }

                saveSongMutation.mutate({details: songDetails, createdByID: loggedInUser?.id})
            }}
        >
            <KeyboardAwareScrollView bottomOffset={100} keyboardShouldPersistTaps="handled">
                <View style={{paddingBottom:20 }}>
                    <InputSection title="Song Information">
                        <View>
                            <Input
                                title="Title"
                                value={songDetails.name}
                                maxLength={120}
                                onUpdate={(new_value) => {
                                    setSongDetails({
                                        name: new_value
                                    })
                                }}
                            />

                            <Input
                                title="Artist"
                                value={songDetails.artist}
                                onUpdate={(new_value) => {
                                    setSongDetails({
                                        artist: new_value
                                    })
                                }}
                            />

                            <View style={{flexDirection:"row", justifyContent:"space-between"}}>
                                <View style={{flex:1,marginRight:10}}>
                                    <Input
                                        autoCorrect={false}
                                        title="Key"
                                        value={songDetails.defaultKey}
                                        onUpdate={(new_value) => {
                                            const validated = new_value.match(/[A-G](b|#)?(m)?/g)
                                            new_value = validated ? validated[0] : "";

                                            setSongDetails({
                                                defaultKey: new_value
                                            })
                                        }}
                                    />
                                </View>
                                <View style={{flex:1,marginLeft:10}}>
                                    <Input
                                        title="Tempo"
                                        type="numeric"
                                        value={songDetails.tempo?.toString()}
                                        onUpdate={(new_value) => {
                                            setSongDetails({
                                                tempo: new_value
                                            })
                                        }}
                                    />
                                </View>
                            </View>
                                                        

                            
                        </View>
                    </InputSection>

                    <InputSection title="Streaming Links" description="Add links to this song on different streaming services so users can easily listen on their preferred platform">
                        <Input title="Spotify Link" value={songDetails.spotifyURL} onUpdate={(new_value) => {
                                setSongDetails({
                                    spotifyURL: new_value
                                })
                            }}
                        />

                        <Input title="Apple Music Link" value={songDetails.appleMusicURL} onUpdate={(new_value) => {
                                setSongDetails({
                                    appleMusicURL: new_value
                                })
                            }}
                        />

                        <Input title="Youtube Link" value={songDetails.youtubeMusicURL} onUpdate={(new_value) => {
                                setSongDetails({
                                    youtubeMusicURL: new_value
                                })
                            }}
                        />

                        <Input title="Amazon Music Link" value={songDetails.amazonMusicURL} onUpdate={(new_value) => {
                                setSongDetails({
                                    amazonMusicURL: new_value
                                })
                            }}
                        />
                    </InputSection>


                    <InputSection title="Song Resources" description="Add any resources the song may have including a track, chords, or any other files">
                        <View style={{marginVertical:10}}>
                            <SectionTitle title="Chords" marginLeft={10} />
                            <UploaderDropZone assets={[songDetails.chordFile]} name="chordFile" /> 
                        </View>


                        <View style={{marginVertical:10}}>
                            <SectionTitle title="Lyrics" marginLeft={10} />
                            <UploaderDropZone assets={[songDetails.lyricFile]} name="lyricFile" /> 
                        </View>


                        <View style={{marginVertical:10}}>
                            <SectionTitle title="Track" marginLeft={10} />
                            <UploaderDropZone assets={[songDetails.selfHostedTrackFile]} name="selfHostedTrackFile" /> 
                        </View>


                        <View style={{marginVertical:10}}>
                            {id 
                                ? <View>
                                    <SectionTitle title="Additional Files" marginLeft={10} />
                                    <Text style={styles.additionalFilesText}>To upload additional files, go back and select All Resources to manage all files for this song.</Text>
                                </View>
                                : <>
                                    <SectionTitle title="Additional Files" marginLeft={10} />
                                    <UploaderDropZone assets={songDetails.files} name="files" multiple={true} />
                                </>
                            }
                        </View>
                        
                    </InputSection>

                </View>
            </KeyboardAwareScrollView>
        </ModalWrapper>
    );
}

const styles = StyleSheet.create(({colors}) => ({
    additionalFilesText: {
        color:colors.textSecondary, 
        fontSize:12, 
        fontStyle:"italic", 
        paddingLeft:10
    }
}))
